import React, { useState, useEffect, useRef } from "react";
import { Skeleton } from "@mui/material";
import ReactPaginate from "react-paginate";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import { Link, useNavigate } from "react-router-dom";
import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import Messages from "../../../constants/Messages";
import handleError from "../../../constants/handleError";
import { usePermission } from "../../../context/PermissionContext";
import AddService from "./AddService";
import EditService from "./EditService";
import DeleteModal from "../../../constants/DeleteModal";
import { useContextApi } from "../../../context/ContextApi";
import useKeyboardTableNavigation from "../../../constants/useKeyboardTableNavigation";
import Table from "../../../constants/Table";
import { formatCurrencyFun } from "../../../utils/formatCurrencyFunc";
import { AddIcon, DeleteIcon, DownloadIcon, EditIcon, RefreshIcon, SearchIcon, SpreadSheetIcon } from "../../../constants/Icons";

const ListService = ({ isActive }) => {
  const [isBtnLoading, setBtnLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isServiceData, setServiceData] = useState([]);
  const hasFetched = useRef(false);
  const { handleSuggestEstimate, estimateSuggest, setEstimateSuggest } =
    useContextApi();
  const containerRef = useRef(null);

  const searchInputRef = useRef(null);
  const ProductSuggestionsRef = useRef(null);
  const { permission, hasPermission, globelPopupOpen } = usePermission();

  const suggestionRefs = useRef([]);

  const [isSearchTerm, setSearchTerm] = useState("");
  const [ProductSuggestions, setProductSuggestions] = useState([]);
  const [selectedProductCode, setSelectedProductCode] = useState("");
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const perPage = 10;

  const [isAddModalOpen, setAddModelOpen] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isEditData, setEditData] = useState(null);
  const [isDeleteData, setDeleteData] = useState(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [isViewData, setViewData] = useState(false);

  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const navigate = useNavigate();

  const debounceTimerRef = useRef(null);
  const controllerRef = useRef(null);
  const requestIdRef = useRef(0);

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleListService = async (page = 0) => {
    try {
      if (!checkNetwork()) return;
      setIsLoading(true);
      const response = await AxiosInstance.get(
        `module/service?pageno=${page + 1}&limit=${perPage}`,
      );
      const results = response.data.results || [];
      setServiceData(results);
      if (results.length === 0) {
        setTotalPages(0);
        setTotalItems(0);
        setCurrentPage(0);
      } else {
        setTotalPages(response.data.totalPages || 0);
        setTotalItems(response.data.totalItems || 0);
      }
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setHasSearched(true);
    setSelectedProductCode("");

    if (!term.trim()) {
      setProductSuggestions([]);
      handleListService(0, "");
      setCurrentPage(0);
      return;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    debounceTimerRef.current = setTimeout(async () => {
      try {
        if (!checkNetwork()) return;
        setSuggestionLoading(true);

        controllerRef.current = new AbortController();
        const currentId = ++requestIdRef.current;
        const response = await AxiosInstance.get(
          `module/service/search-hybrid?searchtxt=${term}`,
          { signal: controllerRef.current.signal },
        );
        if (currentId === requestIdRef.current) {
          setProductSuggestions(response.data.results || []);
        }
      } catch (error) {
        if (error.name !== "CanceledError" && error.name !== "AbortError") {
          handleError(error, showAlert);
        }
      } finally {
        setSuggestionLoading(false);
      }
    }, 200);
  };

  // const handleSearchFind = () => {
  //   const currentInputValue = searchInputRef.current?.value || "";
  //   setSearchTerm(currentInputValue);
  //   setSelectedProductCode("");
  //   if (!currentInputValue.trim()) {
  //     handleListService(0, "");
  //   } else {
  //     handleListService(0, currentInputValue);
  //   }
  //   setProductSuggestions([]);
  //   setHasSearched(false);
  //   setCurrentPage(0);
  // };

  // const handleSelectProduct = (product) => {
  //   const code = product.code;
  //   setSearchTerm(code);
  //   setSelectedProductCode(code);
  //   setProductSuggestions([]);
  //   setHasSearched(false);
  //   if (searchInputRef.current) {
  //     searchInputRef.current.value = "";
  //   }
  //   handleListService(0, code);
  //   setCurrentPage(0);
  // };

  const handleOutsideClick = (e) => {
    if (
      ProductSuggestionsRef.current &&
      !ProductSuggestionsRef.current.contains(e.target)
    ) {
      setProductSuggestions([]);
      setHasSearched(false);
    }
  };

  const handleAddServiceOpen = () => {
    setAddModelOpen(true);
  };

  const handleAddServiceClose = () => {
    setAddModelOpen(false);
  };

  const handleAdd = async (newData, quanity, onMode) => {
    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      const data = {
        hsnsac: newData.hsnsac,
        title: newData.title,
        description: newData.description,
        mrp: newData.mrp,
        saleprice: newData.saleprice,
        taxpercentage: newData.taxpercentage,
        baseprice: newData.baseprice,
        isLock: newData.isLock,
        unitConfig: newData.unitConfig,
      };
      const response = await AxiosInstance.post(`module/service`, data);
      setAddModelOpen(false);
      setServiceData((prev) => [response.data.results, ...prev]);
      setTotalItems((prev) => prev + 1);
      showAlert("Success", "Service Added");
      setBtnLoading(false);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };

  const openEditModal = async (cItem) => {
    try {
      const response = await AxiosInstance.get(`/module/service/${cItem._id}`);
      if (response?.data) {
        const serviceData = response.data.results;
        setEditData({
          ...serviceData,
          mrp: CurrencyConverter.toCurrency(serviceData.mrp, "INR"),
          saleprice: CurrencyConverter.toCurrency(serviceData.saleprice, "INR"),
          discount: CurrencyConverter.toCurrency(serviceData.discount, "INR"),
          baseprice: CurrencyConverter.toCurrency(serviceData.baseprice, "INR"),
          taxamount: CurrencyConverter.toCurrency(serviceData.taxamount, "INR"),
        });
      }
      setEditModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch service details:", error);
      showAlert("error", "Unable to fetch service details");
    }
  };

  const closeEditModal = () => {
    setEditData(null);
    setEditModalOpen(false);
  };

  const handleUpdate = async (updatedData, trigger = true, onMode, type) => {
    const originalData = isEditData;
    let isDataChanged = false;
    const toSubunit = (value) => {
      if (value == null || value === "") return 0;
      const num = parseFloat(value);
      return num < 1000 ? Math.round(num * 100) : Math.round(num);
    };
    const normalizedUpdated = {
      ...updatedData,
      mrp: toSubunit(updatedData.mrp),
      saleprice: toSubunit(updatedData.saleprice),
      baseprice: toSubunit(updatedData.baseprice),
    };

    const normalizedOriginal = {
      ...originalData,
      mrp: toSubunit(originalData.mrp),
      saleprice: toSubunit(originalData.saleprice),
      baseprice: toSubunit(originalData.baseprice),
    };

    if (
      normalizedUpdated.hsnsac !== normalizedOriginal.hsnsac ||
      normalizedUpdated.title !== normalizedOriginal.title ||
      normalizedUpdated.description !== normalizedOriginal.description ||
      normalizedUpdated.taxpercentage !== normalizedOriginal.taxpercentage ||
      normalizedUpdated.isLock !== normalizedOriginal.isLock
      // JSON.stringify(normalizedUpdated?.unitConfig)!==JSON.stringify(normalizedOriginal?.unitConfig)
    ) {
      isDataChanged = true;
    }

    if (
      !isDataChanged &&
      (normalizedUpdated.mrp !== normalizedOriginal.mrp ||
        normalizedUpdated.saleprice !== normalizedOriginal.saleprice ||
        normalizedUpdated.baseprice !== normalizedOriginal.baseprice)
    ) {
      isDataChanged = true;
    }

    if (!isDataChanged) {
      showAlert("success", "Updated Service");
      if (trigger) closeEditModal();
      return true;
    }
    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      const data = {
        hsnsac: updatedData.hsnsac,
        title: updatedData.title,
        description: updatedData.description,
        mrp: updatedData.mrp,
        saleprice: updatedData.saleprice,
        taxpercentage: updatedData.taxpercentage,
        baseprice: updatedData.baseprice,
        isLock: updatedData.isLock,
        // unitConfig:updatedData?.unitConfig,
      };
      const response = await AxiosInstance.patch(
        `module/service/${updatedData._id}`,
        data,
      );
      showAlert("success", "Updated Service");
      setServiceData((prevProductData) =>
        prevProductData.map((product) =>
          product._id === response.data.results?._id
            ? response.data.results
            : product,
        ),
      );
      if (trigger) {
        closeEditModal();
      }
      return true;
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };

  const openDeleteModal = (cItem) => {
    setDeleteData(cItem);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      await AxiosInstance.delete(`module/service/${isDeleteData._id}`);
      showAlert("success", "Service Deleted");

      const newTotal = totalItems - 1;
      const newTotalPages = Math.ceil(newTotal / perPage);

      if (isServiceData.length === 1 && currentPage > 0) {
        setCurrentPage((prev) => prev - 1);
        await handleListService(currentPage - 1, isSearchTerm);
      } else {
        await handleListService(currentPage, isSearchTerm);
      }

      setTotalItems(newTotal);
      setTotalPages(newTotalPages);
      setDeleteModalOpen(false);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };

  const getColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-label-success";
      case "inactive":
        return "bg-label-danger";
      default:
        return "bg-label-secondary";
    }
  };

  const exportToCSV = () => {
    if (!isServiceData?.length > 0) {
      showAlert("warning", "No data available to export");
      return;
    }

    // 1. Headers
    const headers = `S No,HSN/SAC,Title,Description,MRP,Discount,Sale Price,Tax(%),Status`;

    const clean = (val) => (val ?? "").toString().replace(/[\r\n;]/g, " ");

    // 2. Rows
    const rows = isServiceData
      .map((item, index) => {
        const row = [
          currentPage * perPage + index + 1,
          clean(item.hsnsac),
          clean(item.title),
          clean(item.descriptionGlimpse),
          clean(CurrencyConverter.toCurrency(item.mrp || 0, "INR")),
          clean(CurrencyConverter.toCurrency(item.discount || 0, "INR")),
          clean(CurrencyConverter.toCurrency(item.saleprice || 0, "INR")),
          clean(item.taxpercentage),
          clean(item.status),
        ];
        return row.join(",");
      })
      .join("\n");

    // 5. Combine everything
    const csvContent = [headers, rows].join("\n");

    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `ServiceList_${formatDate(new Date())}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSuggestionNavigation = (e) => {
    if (e.key === "Enter") {
      if (activeIndex >= 0 && ProductSuggestions[activeIndex]) {
        e.preventDefault();
        openEditModal(ProductSuggestions[activeIndex]);
      }
      // else {
      //   e.preventDefault();
      //   handleSearchFind();
      // }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < ProductSuggestions.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : ProductSuggestions.length - 1,
      );
    }
  };

  useKeyboardTableNavigation({
    isActive,
    containerRef,
    searchTerms: isSearchTerm,
    suggestions: ProductSuggestions,
    modals: {
      isEditModalOpen,
      isDeleteModalOpen,
      globelPopupOpen,
      isAddModalOpen,
    },
    onEnter: openEditModal,
  });

  useEffect(() => {
    if (activeIndex >= 0 && suggestionRefs.current[activeIndex]) {
      suggestionRefs.current[activeIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeIndex]);

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (!isActive) return;

    // small timeout ensures DOM is visible after tab switch
    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, [isActive]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    handleSuggestEstimate();
  }, []);

  useEffect(() => {
    handleListService(currentPage);
  }, [currentPage]);

  const handleRefresh = async () => {
    await handleListService(currentPage);
  };

  return (
    <div className="page-wrapper">
      <div className="page-content">
        <div className="page-breadcrumb d-flex align-items-center paddingBottom gap-2">
          <div className="pb-1">
            <button
              type="button"
              className="btn btn-sm btn-light p-1 shadow-none"
              onClick={handleRefresh}
              disabled={isLoading}
              title="Refresh list"
            >
              <RefreshIcon iClass={`${isLoading ? "bx-spin" : ""}`}/>
            </button>
          </div>
          <div className="breadcrumb-title pe-3">List Service</div>
          <div className="ms-auto d-flex gap-2">
            <div className="dropdown">
              <button
                className="btn btn-sm btn-export"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <DownloadIcon iClass={"btn-sm"}/> Export
              </button>
              <ul className="dropdown-menu">
                <li>
                  <Link className="dropdown-item" onClick={exportToCSV}>
                    <SpreadSheetIcon/>{" "}
                    <span className="icon-text">Excel (CSV)</span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col">
            <div className="card radius-10 mb-0">
              <div className="card-body px-0">
                <div className="row px-3">
                  <div className=" col-lg-5 col-12 ">
                    <div className="position-relative">
                      <div className="input-group" ref={ProductSuggestionsRef}>
                        <input
                          type="text"
                          className="form-control"
                          ref={searchInputRef}
                          onChange={(e) => {
                            handleSearch(e.target.value);
                            setActiveIndex(-1);
                          }}
                          onKeyDown={handleSuggestionNavigation}
                          autoComplete="off"
                          placeholder="Search Service by name ..."
                        />
                        <button
                          className="input-group-text"
                          // onClick={handleSearchFind}
                        >
                          <SearchIcon/>
                        </button>
                      </div>

                      {hasSearched &&
                        isSearchTerm &&
                        !suggestionLoading &&
                        ProductSuggestions.length === 0 && (
                          <ul className="suggestions-list">
                            <li className="suggestion-item text-muted">
                              No products data was found
                            </li>
                          </ul>
                        )}

                      {ProductSuggestions.length > 0 && (
                        <ul className="suggestions-list">
                          {ProductSuggestions.map((product, index) => (
                            <li
                              key={product._id}
                              ref={(el) => (suggestionRefs.current[index] = el)}
                              onClick={() => openEditModal(product)}
                              className={`suggestion-item ${
                                index === activeIndex ? "active-suggestion" : ""
                              }`}
                            >
                              {/* <span className="badge rounded-pill bg-label-navy">
                                {product.code}
                              </span>{" "}
                              -
                              <span className="badge rounded-pill bg-label-retro">
                                {product.hsnsac}
                              </span>{" "}
                              - */}
                              {product.title} -
                              <span className="badge bg-label-fall rounded-pill">
                                ₹
                                {CurrencyConverter.toCurrency(
                                  product.saleprice || 0,
                                  "INR",
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-2  col-6 d-flex align-items-center">
                    <div>
                      <strong>Total Service:</strong>{" "}
                      <span className="badge btn-label-primary fs-15 ">
                        {totalItems || 0}
                      </span>
                    </div>
                  </div>
                  <div className="col-lg-5 col-6 d-flex justify-content-end ">
                    <button
                      type="button"
                      className="btn btn-primary mt-2 mt-lg-0"
                      onClick={handleAddServiceOpen}
                    >
                      <AddIcon iClass={"me-2"}/>New Service
                    </button>
                  </div>
                </div>
                <div ref={containerRef}>
                  <Table
                    loading={isLoading}
                    data={isServiceData}
                    onRowClick={(item) => openEditModal(item)}
                    columns={[
                      {
                        label: "S.No",
                        render: (_, index) => (
                          <>{currentPage * perPage + index + 1}</>
                        ),
                      },
                      {
                        label: "Description",
                        tdBodyclass:"text-align-left-force",
                        render: (item) => (
                          <div className="d-flex flex-column">
                            <span className="text-nowrap text-heading fw-medium">
                              {item.title}
                            </span>
                            <span className="text-nowrap">
                              <small>{item?.descriptionGlimpse}</small>
                            </span>
                          </div>
                        ),
                      },
                      {
                        label: "MRP",
                        render: (item) => (
                          <span className="text-right">
                            {formatCurrencyFun(item?.mrp)}
                          </span>
                        ),
                      },
                      {
                        label: "Discount",
                        render: (item) => (
                          <span className="text-right">
                            {formatCurrencyFun(item?.discount)}
                          </span>
                        ),
                      },
                      {
                        label: "Sale Price",
                        tdBodyclass:"tdPadding",
                        render: (item) => (
                          <span className="text-right">
                           {formatCurrencyFun(item?.saleprice)}
                          </span>
                        ),
                      },
                      {
                        label: "Tax(%)",
                        thClass:"w-12",
                        render: (item) => (
                          <div className="no-print position-relative">
                          <div className=" d-flex justify-content-center align-items-center">
                          <span className="stock-status-badge badge rounded-pill bg-label-peace">
                            {item.taxpercentage
                              ? `${item.taxpercentage}%`
                              : "0%"}
                          </span>
                          <div className="order-actions order-actions3 opacity-0 opacity-md-100">
                            <button
                              type="button"
                              className="btn-label-primary shadow-none me-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(item);
                              }}
                            >
                              <EditIcon/>
                            </button>
                            <button
                              type="button"
                              className="btn-label-danger shadow-none me-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteModal(item);
                              }}
                            >
                              <DeleteIcon/>
                            </button>
                          </div>
                          </div>
                          </div>
                        ),
                      },
                      // {
                      //   label: "Actions",
                      //   render: (item) => (
                      //     <div className="order-actions opacity-0 opacity-md-100">
                      //       <button
                      //         type="button"
                      //         className="btn-label-primary shadow-none me-2"
                      //         onClick={(e) => {
                      //           e.stopPropagation();
                      //           openEditModal(item);
                      //         }}
                      //       >
                      //         <i className="ng ng-ng-edit-icon"></i>
                      //       </button>
                      //       <button
                      //         type="button"
                      //         className="btn-label-danger shadow-none me-2"
                      //         onClick={(e) => {
                      //           e.stopPropagation();
                      //           openDeleteModal(item);
                      //         }}
                      //       >
                      //         <i className="ng ng-ng-delete-icon"></i>
                      //       </button>
                      //     </div>
                      //   ),
                      // },
                    ]}
                  />
                </div>
                {totalPages > 0 && (
                  <div className="d-flex justify-content-center mt-3">
                    <ReactPaginate
                      previousLabel={"← Previous"}
                      nextLabel={"Next →"}
                      breakLabel={"..."}
                      onPageChange={({ selected }) => {
                        setIsLoading(true);
                        setCurrentPage(selected);
                      }}
                      pageRangeDisplayed={5}
                      pageCount={totalPages}
                      containerClassName="pagination"
                      pageClassName="page-item"
                      pageLinkClassName="page-link"
                      previousClassName="page-item"
                      previousLinkClassName="page-link"
                      nextClassName="page-item"
                      nextLinkClassName="page-link"
                      breakClassName="page-item"
                      breakLinkClassName="page-link"
                      activeClassName="active"
                      forcePage={currentPage}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {isAddModalOpen && (
          <AddService
            isActive={isActive}
            onClose={handleAddServiceClose}
            onAdd={handleAdd}
            isBtnLoading={isBtnLoading}
            estimateSuggest={estimateSuggest}
            showQuanity={false}
          />
        )}

        {isEditModalOpen && (
          <EditService
            isActive={isActive}
            editData={isEditData}
            onClose={closeEditModal}
            onUpdate={handleUpdate}
            isBtnLoading={isBtnLoading}
            estimateSuggest={estimateSuggest}
            showQuanity={false}
          />
        )}

        {isDeleteModalOpen && (
          <DeleteModal
            onClose={() => setDeleteModalOpen(false)}
            onConfirm={handleDelete}
            isLable={" Service " + isDeleteData?.title}
            isBtnLoading={isBtnLoading}
          />
        )}
      </div>
    </div>
  );
};

export default ListService;
